import React, {PureComponent, ComponentClass, RefObject} from "react"
import {injectIntl as injectIntlReact} from "react-intl"
import {InjectIntl} from "."

function escapeId(id: string) {
  const config = window.PHRASEAPP_CONFIG
  return `${config.prefix}phrase_${id}${config.suffix}`
}

function isPhraseEnabled() {
  return window.PHRASEAPP_ENABLED
}

export interface InjectIntlProps extends InjectIntl {
  forwardedRef?: any
}

interface InjectIntlContext {}

export function injectIntl<Props>(
  Comp: React.ComponentType<Props & InjectIntlProps>,
  {withRef = false} = {}
): ComponentClass<Pick<Props, Exclude<keyof Props, keyof InjectIntlProps>>> & {
  WrappedComponent: React.ComponentType<Props & InjectIntlProps>
} {
  type PrivateProps = {
    forwardedRef?: RefObject<typeof Comp>
  }

  type State = {
    errors: any
  }

  class WrappedComponent extends PureComponent<PrivateProps & Props, State> {
    static defaultProps = {
      forwardedRef: null
    }

    constructor(props: InjectIntlProps & Props, context: InjectIntlContext) {
      super(props, context)
      this.render = this.render.bind(this)
      this.translate = this.translate.bind(this)
      this.formatMessage = this.formatMessage.bind(this)

      this.state = {errors: {}}
    }

    translate(keyName: string, values: {[key: string]: any}) {
      if (isPhraseEnabled()) {
        const escapedString = keyName.replace("<", "[[[[[[html_open]]]]]]").replace(">", "[[[[[[html_close]]]]]]")
        return escapeId(escapedString)
      }
      return this.props.intl.formatMessage({id: keyName}, values)
    }

    formatMessage(messageDescriptor: string, values: {[key: string]: any}) {
      const {id} = messageDescriptor
      if (!id) {
        console.error("formatMessage requires an id")
      } else {
        return this.translate(id, values)
      }
    }

    getWrappedInstance = () => this.wrappedInstance

    render() {
      const {forwardedRef, ...props} = this.props
      props.errors = this.state.errors
      props.translate = this.translate
      props.formatMessage = this.formatMessage
      if (withRef && forwardedRef) {
        props.ref = forwardedRef
      }

      return <Comp {...props} />
    }
  }

  const Injected = injectIntlReact(WrappedComponent, {withRef})

  if (withRef) {
    return React.forwardRef<typeof Comp, OriginalProps>((props, ref) => {
      return <Injected {...props} forwardedRef={ref} />
    })
  }

  return Injected
}
